import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Printer, Copy, Check, Sparkle, FloppyDisk, Plus, Trash, CreditCard } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { computePricing, documentHtml, fmtDate, money, normalizeHst } from "../lib/experienceInvoice";
import { toast } from "sonner";

const defaultInvNo = (b) => `INV-${String(b.id || "").slice(-6).toUpperCase()}`;
const NO_SETTINGS = { hst_number: "", payment_instructions: "" };

const blankInvoice = (b) => ({
  invoice_number: defaultInvNo(b),
  issue_date: fmtDate(),
  service_title: b.tour_name || "",
  itinerary_subtitle: "",
  line_description: "",
  inclusions: [],
  itinerary_steps: [],
  operational_note: "",
  bill_to_name: "",
  bill_to_email: "",
  include_itinerary: true,
});

export function InvoiceDialog({ booking, onClose, onSaved }) {
  const [inv, setInv] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // INV-001: HST number + how-to-pay text, shared by every 01 Experience invoice.
  const [settings, setSettings] = useState(NO_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (booking) {
      const existing = booking.invoice_data && Object.keys(booking.invoice_data).length
        ? { ...blankInvoice(booking), ...booking.invoice_data }
        : blankInvoice(booking);
      setInv(existing);
      setLink(""); setCopied(false);
      api.get("/experience-invoice/settings")
        .then(({ data }) => setSettings({ ...NO_SETTINGS, ...(data || {}) }))
        .catch(() => setSettings(NO_SETTINGS));
    }
  }, [booking]);

  if (!booking || !inv) return null;
  const p = computePricing(booking, inv);
  const hstMissing = !!booking.tax_applied && !String(settings.hst_number || "").trim();

  const set = (k, v) => setInv((o) => ({ ...o, [k]: v }));
  const setStep = (i, k, v) => setInv((o) => {
    const steps = [...o.itinerary_steps]; steps[i] = { ...steps[i], [k]: v }; return { ...o, itinerary_steps: steps };
  });
  const addStep = () => setInv((o) => ({ ...o, itinerary_steps: [...o.itinerary_steps, { title: "", description: "" }] }));
  const removeStep = (i) => setInv((o) => ({ ...o, itinerary_steps: o.itinerary_steps.filter((_, x) => x !== i) }));

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/invoice/draft`);
      setInv((o) => ({ ...o, ...data }));
      toast.success("AI draft ready — review & edit below");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}`, { ...booking, invoice_data: inv });
      toast.success("Invoice saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const togglePaid = async (v) => {
    const next = { ...inv, paid: v };
    setInv(next);
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}`, { ...booking, invoice_data: next });
      onSaved && onSaved();
      toast.success(v ? "Marked as paid" : "Reopened as unpaid");
    } catch (e) { toast.error(formatApiError(e)); setInv((o) => ({ ...o, paid: !v })); }
    finally { setSaving(false); }
  };

  const openDocument = async () => {
    await save();
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.srcdoc = documentHtml(booking, inv, p, { origin: window.location.origin, settings });
    iframe.onload = () => {
      const w = iframe.contentWindow;
      const go = () => {
        try { w.focus(); w.print(); }
        catch (e) { console.error("Print failed:", e); }
        finally { setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1500); }
      };
      // Wait for the brand font (at most 2.5 s) so the PDF never prints in a fallback face.
      const fonts = w.document.fonts && w.document.fonts.ready;
      if (fonts) Promise.race([fonts, new Promise((r) => setTimeout(r, 2500))]).then(go, go);
      else go();
    };
    document.body.appendChild(iframe);
  };

  const generateLink = async () => {
    setLinkLoading(true);
    try {
      const { data } = await api.post(`/payments/checkout/booking/${booking.id}`, { origin_url: window.location.origin });
      setLink(data.url);
      try { await navigator.clipboard.writeText(data.url); setCopied(true); toast.success("Payment link copied"); setTimeout(() => setCopied(false), 2500); }
      catch { toast.success("Payment link generated — copy it below"); }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLinkLoading(false); }
  };

  const saveSettings = async () => {
    const hst = normalizeHst(settings.hst_number);
    if (hst === null) { toast.error("HST number should look like 123456789 RT0001"); return; }
    setSavingSettings(true);
    try {
      const { data } = await api.put("/experience-invoice/settings", { ...settings, hst_number: hst });
      setSettings({ ...NO_SETTINGS, ...(data || {}) });
      toast.success("Invoice details saved for all 01 Experience invoices");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 2500); }
    catch { toast.error("Could not copy — select the link manually"); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={() => onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Invoice & Itinerary</DialogTitle>
          <DialogDescription>AI-draft the itinerary, edit anything, then open the PDF (invoice, plus the itinerary page when it is on) or copy a payment link.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2 border border-border bg-background p-3">
          <div className="text-sm">
            <span className="label-mono">Total Due</span>
            <span className="font-heading text-xl text-primary ml-2" data-testid="invoice-total-due">{money(inv.paid ? 0 : p.balance)}</span>
            <span className="text-xs text-muted-foreground ml-2" data-testid="invoice-total-basis">{p.lines ? `${p.lines.length} ${p.lines.length === 1 ? "line" : "lines"} from the booking` : "booking total, one line"}{booking.tax_applied ? " + HST" : ""}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5" data-testid="invoice-mark-paid-row">
              <Switch checked={!!inv.paid} onCheckedChange={togglePaid} data-testid="invoice-mark-paid-toggle" />
              <span className="text-sm font-medium">Mark as Paid</span>
              {inv.paid && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold">Paid</span>}
            </div>
            <Button size="sm" onClick={aiDraft} disabled={drafting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invoice-ai-draft">
              <Sparkle size={14} className="mr-1.5" />{drafting ? "Drafting…" : inv.line_description ? "Re-draft with AI" : "AI Draft"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-mono mb-1 block">Invoice #</Label>
            <Input value={inv.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="bg-input border-border" data-testid="invoice-number-input" />
          </div>
          <div>
            <Label className="label-mono mb-1 block">Issue Date</Label>
            <Input value={inv.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="bg-input border-border" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-mono mb-1 block">Bill to</Label>
            <Input value={inv.bill_to_name || ""} onChange={(e) => set("bill_to_name", e.target.value)} placeholder={booking.customer_name || "Guest"} className="bg-input border-border" data-testid="invoice-bill-to-name" />
          </div>
          <div>
            <Label className="label-mono mb-1 block">Bill-to email</Label>
            <Input value={inv.bill_to_email || ""} onChange={(e) => set("bill_to_email", e.target.value)} placeholder={booking.customer_email || "Optional"} className="bg-input border-border" data-testid="invoice-bill-to-email" />
          </div>
          <p className="sm:col-span-2 -mt-1 text-xs text-muted-foreground">Leave blank to bill the guest. For a partner job, type the company here; the guest's name still prints under it.</p>
        </div>

        <div>
          <Label className="label-mono mb-1 block">Service Title</Label>
          <Input value={inv.service_title} onChange={(e) => set("service_title", e.target.value)} placeholder="e.g. 6-Hour Niagara Falls Chauffeur Service" className="bg-input border-border" data-testid="invoice-service-title" />
        </div>
        <div>
          <Label className="label-mono mb-1 block">Invoice Line Description</Label>
          <Textarea rows={3} value={inv.line_description} onChange={(e) => set("line_description", e.target.value)} className="bg-input border-border" data-testid="invoice-line-desc" />
        </div>
        <div>
          <Label className="label-mono mb-1 block">Service Inclusions (one per line)</Label>
          <Textarea rows={4} value={(inv.inclusions || []).join("\n")} onChange={(e) => set("inclusions", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className="bg-input border-border" data-testid="invoice-inclusions" />
        </div>

        <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Include guest itinerary page</div>
            <p className="text-xs text-muted-foreground">Turn off for an invoice-only PDF, e.g. for a partner's accounts team.</p>
          </div>
          <Switch checked={inv.include_itinerary !== false} onCheckedChange={(v) => set("include_itinerary", v)} data-testid="invoice-include-itinerary" />
        </div>

        <div>
          <Label className="label-mono mb-1 block">Itinerary Subtitle</Label>
          <Input value={inv.itinerary_subtitle} onChange={(e) => set("itinerary_subtitle", e.target.value)} placeholder="e.g. Niagara Falls Private Chauffeur Experience" className="bg-input border-border" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="label-mono">Itinerary Steps</Label>
            <Button size="sm" variant="outline" onClick={addStep} data-testid="invoice-add-step"><Plus size={13} className="mr-1" />Add step</Button>
          </div>
          {(inv.itinerary_steps || []).map((s, i) => (
            <div key={i} className="border border-border p-3 space-y-2 bg-background" data-testid={`invoice-step-${i}`}>
              <div className="flex items-center gap-2">
                <span className="font-heading text-primary w-6 text-center">{i + 1}</span>
                <Input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} placeholder="Step title" className="bg-input border-border flex-1" />
                <Button size="sm" variant="ghost" onClick={() => removeStep(i)}><Trash size={14} className="text-destructive" /></Button>
              </div>
              <Textarea rows={2} value={s.description} onChange={(e) => setStep(i, "description", e.target.value)} placeholder="Step description" className="bg-input border-border" />
            </div>
          ))}
          {!(inv.itinerary_steps || []).length && <p className="text-xs text-muted-foreground">No steps yet — click “AI Draft” to generate, or add manually.</p>}
        </div>

        <div>
          <Label className="label-mono mb-1 block">Operational Note</Label>
          <Textarea rows={2} value={inv.operational_note} onChange={(e) => set("operational_note", e.target.value)} className="bg-input border-border" data-testid="invoice-op-note" />
        </div>

        <div className="border-t border-border pt-3 space-y-3" data-testid="invoice-company-details">
          <div>
            <div className="text-sm font-medium">01 Experience invoice details</div>
            <p className="text-xs text-muted-foreground">Printed on every 01 Experience invoice. {isAdmin ? "Only admins can change these." : "Ask an admin to change these."}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-mono mb-1 block">HST number</Label>
              <Input value={settings.hst_number} onChange={(e) => setSettings((o) => ({ ...o, hst_number: e.target.value }))} disabled={!isAdmin} placeholder="123456789 RT0001" className="bg-input border-border" data-testid="invoice-hst-number" />
            </div>
            <div>
              <Label className="label-mono mb-1 block">How to pay</Label>
              <Textarea rows={2} value={settings.payment_instructions} onChange={(e) => setSettings((o) => ({ ...o, payment_instructions: e.target.value }))} disabled={!isAdmin} placeholder="e.g. e-Transfer to ... Reference the invoice number." className="bg-input border-border" data-testid="invoice-how-to-pay" />
            </div>
          </div>
          {hstMissing && (
            <p className="text-xs text-amber-600" data-testid="invoice-hst-missing">This invoice charges HST but no HST number is set. A business client needs the number on the invoice to claim the tax back.</p>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={saveSettings} disabled={savingSettings} data-testid="invoice-save-details">
              <FloppyDisk size={14} className="mr-1.5" />{savingSettings ? "Saving…" : "Save invoice details"}
            </Button>
          )}
        </div>

        {link && (
          <div className="border-t border-border pt-3">
            <Label className="label-mono mb-1 block">Stripe payment link</Label>
            <div className="flex items-center gap-2">
              <input readOnly value={link} className="flex-1 bg-input border border-border rounded px-2 py-1.5 text-xs truncate" data-testid="invoice-payment-link" />
              <Button size="sm" variant="outline" onClick={copyLink}>{copied ? <Check size={14} /> : <Copy size={14} />}</Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={generateLink} disabled={linkLoading} data-testid="invoice-copy-link-button">
            <CreditCard size={14} className="mr-1.5" />{linkLoading ? "Generating…" : link ? "Re-copy link" : "Copy payment link"}
          </Button>
          <Button variant="outline" onClick={save} disabled={saving} data-testid="invoice-save"><FloppyDisk size={14} className="mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
          <Button onClick={openDocument} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invoice-open-document"><Printer size={14} className="mr-1.5" />Open / Save PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
